import { DayOfWeek } from 'wizards/models/DayOfWeek.enum';
import { WeekNumber } from 'wizards/models/WeekNumber.enum';

export type DailySchedule = { type: 'Daily'; days: Array<boolean> };
export type MonthlySchedule = { type: 'Monthly'; occurrence: WeekNumber; dayOfWeek?: DayOfWeek; dayOfMonth?: number };

export class Schedule {
  public get isDaily(): boolean {
    return this.type === 'Daily';
  }

  public get isMonthly(): boolean {
    return this.type === 'Monthly';
  }

  public get isDayOfMonth(): boolean {
    return this.type === 'Monthly' && this.occurrence === WeekNumber['Day Of Month'];
  }

  private type: 'Daily' | 'Monthly';
  private days: Array<boolean> = [];
  private occurrence: WeekNumber;
  private dayOfWeek: DayOfWeek;
  private dayOfMonth: number;

  constructor(schedule?: DailySchedule | MonthlySchedule) {
    if (schedule) {
      this.setSchedule(schedule);
    }
  }

  setSchedule(schedule: DailySchedule | MonthlySchedule): void {
    this.resetSchedule();

    this.type = schedule.type;

    if (schedule.type === 'Daily') {
      this.days = schedule.days;
    }

    if (schedule.type === 'Monthly') {
      this.occurrence = schedule.occurrence;
      this.dayOfWeek = schedule.dayOfWeek;
      this.dayOfMonth = schedule.dayOfMonth;
    }
  }

  getMinDailyPeriod(): number {
    /*
        Logic:
        We have the schedule for a week.
        After this week will be next week.
        Before this week was another week.
        And all together they - endless line.
        One week schedule looks like [true, false, true, false, false, false, false]
        Replace true with 1 and false with 0.
        So, we have 21 days. Look for patterns.
        11 means we have only 1 day between incremental of FFI. Monday, Tuesday. Or Sunday, Monday (the hardest case)
        101 means we have 2 days between incremental of FFI. Monday, Wednesday
        1001 means we have 3 days between incremental of FFI. Monday, Thursday
       */
    const endlessDataLine = this.generateMonthBySchedule().join('');
    let min = 7; // just the biggest value

    ['11', '101', '1001', '10001', '100001', '1000001'].forEach((seq, i) => {
      if (endlessDataLine.includes(seq)) {
        min = Math.min(min, i + 1);
      }
    });

    return min;
  }

  match(otherSchedule: Schedule): boolean {
    if (this.isMonthly && otherSchedule.isMonthly) {
      if (this.isDayOfMonth && otherSchedule.isDayOfMonth) {
        return this.dayOfMonth === otherSchedule.dayOfMonth;
      }

      if (!this.isDayOfMonth && !otherSchedule.isDayOfMonth) {
        return this.occurrence === otherSchedule.occurrence && this.dayOfWeek === otherSchedule.dayOfWeek;
      }
    }

    if ([this, otherSchedule].some((s) => s.isDayOfMonth) && [this, otherSchedule].some((s) => s.occurrence === WeekNumber.Penultimate)) {
      return this.matchPenultimateWithDayOfMonth(this, otherSchedule);
    }

    if ([this, otherSchedule].some((s) => s.isDayOfMonth) && [this, otherSchedule].some((s) => s.occurrence === WeekNumber.Last)) {
      return this.matchLastWithDayOfMonth(this, otherSchedule);
    }

    return this.matchFullMonths(this.generateMonthBySchedule(), otherSchedule.generateMonthBySchedule());
  }

  generateMonthBySchedule(): Array<0 | 1> {
    if (this.isDaily) {
      return [
        ...this.days, // First week 1-7 days
        ...this.days, // Second week 8-14 days
        ...this.days, // Third week 15-21 days
        ...this.days, // Fourth week 22-28 days
        ...this.days, // Fifth week 29-31 days and tail from the next month
        ...this.days // Special tale for 30th day of month and daily schedule with Monday only or 31/Tuesday
      ].map((value) => (value ? 1 : 0));
    }

    const result = [...Array(7 * 6)].fill(0);

    switch (this.occurrence) {
      case WeekNumber.First:
      case WeekNumber.Second:
      case WeekNumber.Third:
      case WeekNumber.Fourth: {
        // First Wednesday could be at the First week, but also it can be at the Second (+1) week
        result[this.occurrence * 7 + this.dayOfWeek] = 1;
        result[(this.occurrence + 1) * 7 + this.dayOfWeek] = 1;

        break;
      }
      case WeekNumber.Penultimate: {
        // Penultimate Friday could be at the Fourth week, but also it can be at the Third (-1) week
        result[2 * 7 + this.dayOfWeek] = 1;
        result[3 * 7 + this.dayOfWeek] = 1;

        break;
      }
      case WeekNumber.Last: {
        // Last Saturday could be at the Fifth week, but also it can be at the Fourth (-1) week
        result[3 * 7 + this.dayOfWeek] = 1;
        result[4 * 7 + this.dayOfWeek] = 1;

        break;
      }
      default: {
        for (let i = 0; i < 7; i++) {
          // 1st day of Month could be at Monday, Tuesday, Wednesday, ...
          // 2nd day of Month could be then at Tuesday, Wednesday, ...
          // Nst day of Month could be in [N%7 ... N%7 + 7]

          result[this.dayOfMonth - 1 + i] = 1;
        }
      }
    }

    return result;
  }

  private matchFullMonths(a: Array<0 | 1>, b: Array<0 | 1>): boolean {
    return a.some((value, i) => value && value === b[i]);
  }

  private matchPenultimateWithDayOfMonth(a: Schedule, b: Schedule): boolean {
    const penultimate = a.occurrence === WeekNumber.Penultimate ? a : b;
    const usual = a.occurrence === WeekNumber.Penultimate ? b : a;

    if (31 - (14 - penultimate.dayOfWeek) > usual.dayOfMonth) {
      return false;
    }

    return true;
  }

  private matchLastWithDayOfMonth(a: Schedule, b: Schedule): boolean {
    const last = a.occurrence === WeekNumber.Last ? a : b;
    const usual = a.occurrence === WeekNumber.Last ? b : a;

    if (31 - (7 - last.dayOfWeek) > usual.dayOfMonth) {
      return false;
    }

    return true;
  }

  private resetSchedule(): void {
    delete this.type;
    delete this.occurrence;
    delete this.dayOfWeek;
    delete this.dayOfMonth;

    this.days = [];
  }
}
